import { useEffect } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { useSelector, useStore } from '@/redux/helper';

import { satisfiesGate } from '../core/entitlement';
import speedyFlowSlice from '../redux/slice';
import selectSpeedyFlowUser from '../selectUser';
import { GateRequirement } from '../types';

const { advanceSpeedyFlow } = speedyFlowSlice.actions;

/**
 * Tell the flow who the visitor is now, whenever that changes.
 *
 * The flow used to run on a snapshot taken when it opened, and every fact in it could go stale
 * underneath: an account made in the tab a magic link opened, a persona read while the visitor was
 * still signed out, an entitlement bought somewhere else, an address confirmed from the emailed
 * link. Each of those was its own bug with its own fix; this is the one boundary they all cross.
 * See ADR 0007 — facts are reactive, steps are intents.
 *
 * Keyed on the redux user object rather than on fields picked out of it. `setUserData` replaces it
 * wholesale, so its identity changing *is* the event — and the machine ignores a report that says
 * nothing new, which is what makes it safe to be generous about when this fires.
 */
const useReportFacts = (visible: boolean, gate: GateRequirement | undefined) => {
  const dispatch = useDispatch();
  const store = useStore();
  const user = useSelector((state) => state.user);

  useEffect(() => {
    if (!visible || !gate) {
      return;
    }
    // Read through the same selector and the same `satisfiesGate` that decided whether to open the
    // flow at all, so the question cannot be answered two ways for one visitor.
    const flowUser = selectSpeedyFlowUser(store.getState());
    dispatch(
      advanceSpeedyFlow({
        type: 'factsChanged',
        facts: {
          hasAccount: flowUser.isLoggedIn,
          hasPersona: !!flowUser.persona,
          isRealEmail: flowUser.isRealEmail,
          gateSatisfied: satisfiesGate(gate, flowUser, Date.now()),
        },
      }),
    );
  }, [user, visible, gate, store, dispatch]);
};

export default useReportFacts;
